import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { NON_ADMIN_ROLE, useAuth, useRolesQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { DEFAULT_NON_ADMIN_ROLE } from "./RolesSubPage/roleEditorConfig";

export interface UseInviteUserFormProps {
  roleOptions: string[];
  tagOptions: string[];
  onSuccess?: () => void;
}

export const useInviteUserForm = ({ roleOptions, tagOptions, onSuccess }: UseInviteUserFormProps) => {
  const { selectedOrganization } = useAuth();
  const { t } = useTranslation("settings");

  const rolesQuery = useRolesQuery({
    select: (roles) =>
      roles.map(({ role }) =>
        role === NON_ADMIN_ROLE ? { value: role, label: t("Non-Admin") } : { value: role, label: role }
      ),
    enabled: !roleOptions.length,
  });
  const roles = roleOptions.length ? roleOptions.map((role) => ({ value: role, label: role })) : rolesQuery.roles;
  const inviteUserMutation = useTellescopeMutation({
    model: "users",
    method: "invite_user",
  });

  const inviteUserForm = useForm({
    fields: {
      email: {
        type: "email",
        initialValue: "",
        required: true,
      },
      fname: {
        type: "text",
        initialValue: "",
        required: true,
      },
      lname: {
        type: "text",
        initialValue: "",
        required: true,
      },
      internalDisplayName: {
        type: "text",
        initialValue: "",
      },
      role: {
        type: "select",
        initialValue: DEFAULT_NON_ADMIN_ROLE.role,
      },
      tags: {
        type: "multiselect",
        initialValue: [],
      },
    },
    onSubmit: async (values) => {
      if (!selectedOrganization?.id) return;

      await inviteUserMutation.mutateAsync({
        ...values,
        // Non-Admin is the empty-role baseline, so it maps to no role rather than a stored role string.
        role: values.role === DEFAULT_NON_ADMIN_ROLE.role ? "" : values.role,
        organizationId: selectedOrganization.id,
      });
      onSuccess?.();
    },
  });

  const inviteTagOptions = useMemo(() => tagOptions.map((tag) => ({ value: tag, label: tag })), [tagOptions]);
  const selectedInviteRoleOption = roles?.find((option) => option.value === inviteUserForm.values.role);

  return {
    inviteUserForm,
    inviteUserMutation,
    inviteRoleOptions: roles,
    inviteTagOptions,
    selectedInviteRoleOption,
  };
};
